import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  FiBarChart2,
  FiCalendar,
  FiRefreshCw,
  FiTarget,
  FiTrendingUp,
  FiUsers
} from 'react-icons/fi';
import { API_ENDPOINTS } from '../../utils/api';
import { fetchAllInvoices } from '../../utils/zovcurInvoiceApi';
import { formatINR } from '../../utils/currency';
import {
  MONTH_NAMES,
  QUARTER_LABELS,
  buildMonthlyBreakdown,
  buildSalesmanPerformanceRows,
  formatPeriodLabel,
  getReachTone
} from '../../utils/salesPerformanceUtils';

const formatSno = (index) => String(index + 1).padStart(2, '0');

const ProgressBar = ({ percent, tone = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-600',
    emerald: 'bg-emerald-600'
  };
  return (
    <div className="w-full bg-slate-200 rounded-full h-2">
      <div
        className={`h-2 rounded-full ${colors[tone] || colors.blue}`}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  );
};

const StatCard = ({ label, value, sub, icon: Icon, tone = 'navy' }) => {
  const tones = {
    navy: 'text-[#1a365d] bg-[#e8f0f4]',
    teal: 'text-teal-700 bg-teal-50',
    emerald: 'text-emerald-700 bg-emerald-50',
    amber: 'text-amber-700 bg-amber-50'
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{value}</p>
          {sub ? <p className="text-xs text-slate-500 mt-1">{sub}</p> : null}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon />
        </div>
      </div>
    </div>
  );
};

const UserPerformance = () => {
  const now = new Date();
  const [periodType, setPeriodType] = useState('monthly');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [selectedSalesmanId, setSelectedSalesmanId] = useState('all');
  const [salesmen, setSalesmen] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [salesmenRes, invoiceData, targetsRes] = await Promise.all([
        axios.get(API_ENDPOINTS.getSalesmen, { headers: authHeaders() }),
        fetchAllInvoices(),
        axios.get(`${API_ENDPOINTS.getSalesTargets}?year=${year}`, { headers: authHeaders() })
      ]);
      setSalesmen(salesmenRes.data || []);
      setInvoices(invoiceData || []);
      setTargets(targetsRes.data || []);
    } catch (error) {
      console.error('Failed to load performance data:', error);
      setSalesmen([]);
      setInvoices([]);
      setTargets([]);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const performanceRows = useMemo(
    () =>
      buildSalesmanPerformanceRows({
        salesmen,
        invoices,
        targets,
        periodType,
        year,
        month,
        quarter
      }),
    [salesmen, invoices, targets, periodType, year, month, quarter]
  );

  const filteredRows =
    selectedSalesmanId === 'all'
      ? performanceRows
      : performanceRows.filter((row) => String(row.id) === String(selectedSalesmanId));

  const selectedRow =
    selectedSalesmanId === 'all'
      ? null
      : performanceRows.find((row) => String(row.id) === String(selectedSalesmanId));

  const monthlyBreakdown = useMemo(() => {
    if (!selectedRow) return [];
    return buildMonthlyBreakdown(invoices, selectedRow.name, year, targets);
  }, [selectedRow, invoices, year, targets]);

  const summary = useMemo(() => {
    const rows = filteredRows;
    const totalAmount = rows.reduce((sum, row) => sum + row.amountAchieved, 0);
    const amountTarget = rows.reduce((sum, row) => sum + row.amountTarget, 0);
    const balancePrice = Math.max(0, amountTarget - totalAmount);
    return {
      salesmen: rows.length,
      totalAmount,
      amountTarget,
      balancePrice,
      amountReach: amountTarget ? Math.round((totalAmount / amountTarget) * 100) : 0,
      onTarget: rows.filter((row) => row.amountReach >= 100).length
    };
  }, [filteredRows]);

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    return [current - 1, current, current + 1];
  }, [now]);

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-slate-100 via-teal-50/30 to-slate-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-3">
              <FiUsers className="text-teal-600" />
              Salesman Performance
            </h1>
            <p className="text-slate-600 mt-1">
              Month and duration-wise price target reach from invoices
            </p>
          </div>
          <button
            type="button"
            onClick={loadData}
            className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <label className="text-sm">
              <span className="block text-slate-500 mb-1">Duration</span>
              <select
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-slate-500 mb-1">Year</span>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            {periodType === 'monthly' ? (
              <label className="text-sm">
                <span className="block text-slate-500 mb-1">Month</span>
                <select
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  {MONTH_NAMES.map((name, index) => (
                    <option key={name} value={index + 1}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {periodType === 'quarterly' ? (
              <label className="text-sm">
                <span className="block text-slate-500 mb-1">Quarter</span>
                <select
                  value={quarter}
                  onChange={(e) => setQuarter(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  {[1, 2, 3, 4].map((q) => (
                    <option key={q} value={q}>
                      Q{q} ({QUARTER_LABELS[q]})
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="text-sm lg:col-span-2">
              <span className="block text-slate-500 mb-1">Salesman</span>
              <select
                value={selectedSalesmanId}
                onChange={(e) => setSelectedSalesmanId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="all">All Salesmen</option>
                {performanceRows.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
            <FiCalendar />
            Showing: {formatPeriodLabel(periodType, year, month, quarter)}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Salesmen"
            value={summary.salesmen}
            sub={`${summary.onTarget} reached price target`}
            icon={FiUsers}
            tone="navy"
          />
          <StatCard
            label="Price Target"
            value={formatINR(summary.amountTarget)}
            sub="Total target for selected period"
            icon={FiTarget}
            tone="amber"
          />
          <StatCard
            label="Sales Amount"
            value={formatINR(summary.totalAmount)}
            sub={
              summary.amountTarget
                ? `${summary.amountReach}% of price target`
                : 'No price target set'
            }
            icon={FiTrendingUp}
            tone="emerald"
          />
          <StatCard
            label="Balance Price"
            value={summary.amountTarget ? formatINR(summary.balancePrice) : '—'}
            sub={
              summary.amountTarget
                ? summary.balancePrice === 0
                  ? 'Target achieved'
                  : 'Remaining to reach target'
                : 'No price target set'
            }
            icon={FiTarget}
            tone="teal"
          />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <FiBarChart2 className="text-teal-600" />
              Price Target Reach — {formatPeriodLabel(periodType, year, month, quarter)}
            </h2>
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading performance data…</div>
          ) : filteredRows.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              No salesman data for this period. Add salesmen and create invoices with salesman names.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="px-4 py-3 text-left font-semibold text-slate-700 w-14">SNO</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Salesman</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Price Target</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Sales Amount</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Balance Price</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Price Reach</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, index) => (
                    <tr key={row.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-600">{formatSno(index)}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{row.name}</p>
                        <p className="text-xs text-slate-500">
                          {row.empNo} · {row.role}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">
                        {row.amountTarget ? formatINR(row.amountTarget) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatINR(row.amountAchieved)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-amber-700">
                        {row.amountTarget != null && row.amountTarget > 0
                          ? row.amountBalance === 0
                            ? formatINR(0)
                            : formatINR(row.amountBalance)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getReachTone(
                            row.amountReach
                          )}`}
                        >
                          {row.amountTarget ? `${row.amountReach}%` : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{row.invoices}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedRow ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200">
              <h2 className="font-bold text-slate-800">
                Month-wise breakdown — {selectedRow.name} ({year})
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Price target reach for each month of the selected year
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="px-4 py-3 text-left w-14">SNO</th>
                    <th className="px-4 py-3 text-left">Month</th>
                    <th className="px-4 py-3 text-right">Price Target</th>
                    <th className="px-4 py-3 text-right">Sales Amount</th>
                    <th className="px-4 py-3 text-right">Balance Price</th>
                    <th className="px-4 py-3 text-left">Price Reach</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyBreakdown.map((item, index) => (
                    <tr key={item.month} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-600">{formatSno(index)}</td>
                      <td className="px-4 py-3 font-medium">{item.label}</td>
                      <td className="px-4 py-3 text-right text-slate-600">
                        {item.amountTarget ? formatINR(item.amountTarget) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">{formatINR(item.amount)}</td>
                      <td className="px-4 py-3 text-right font-medium text-amber-700">
                        {item.amountTarget
                          ? item.amountBalance === 0
                            ? formatINR(0)
                            : formatINR(item.amountBalance)
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {item.amountTarget ? (
                          <div className="min-w-[120px]">
                            <div className="flex justify-between text-xs mb-1">
                              <span>{item.amountReach}%</span>
                            </div>
                            <ProgressBar percent={item.amountReach} tone="blue" />
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UserPerformance;
